/**
 * Custom Pagination Web Component
 * Uses PagerUtils to handle pagination calculations
 */
import PagerUtils from "./pager-utils.js";

const COVEO_MAX_RESULTS = 5000;

class CoveoPagination extends HTMLElement {
  constructor() {
    super();
    // Default properties
    this.currentPage = 0; // 0-based index
    this.totalItems = 0;
    this.itemsPerPage = 10;
    this.visiblePages = 5;
    this.container = this.querySelector("[data-pagination-list]");

    this.nextButton = this.querySelector("[data-next]");
    this.previousButton = this.querySelector("[data-previous]");

    this.paginationItemTemplate = this.container.innerHTML;
    this.container.innerHTML = "";

    // Bind methods
    this.goToPage = this.goToPage.bind(this);
    this.render = this.render.bind(this);
  }

  /**
   * When component is connected to DOM
   */
  connectedCallback() {
    this.updateFromAttributes();
    this.initButtonsEvent();
  }

  /**
   * When attributes change
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      this.updateFromAttributes();
      this.render();
    }
  }

  /**
   * Observed attributes
   */
  static get observedAttributes() {
    return ["current-page", "total-items", "items-per-page", "visible-pages"];
  }

  /**
   * Update properties from attributes
   */
  updateFromAttributes() {
    // Get attributes (with defaults)
    const currentPage = this.getAttribute("current-page");
    const totalItems = this.getAttribute("total-items");
    const itemsPerPage = this.getAttribute("items-per-page");
    const visiblePages = this.getAttribute("visible-pages");

    // Update properties if attributes exist
    if (currentPage !== null) this.currentPage = parseInt(currentPage, 10);
    if (totalItems !== null) this.totalItems = parseInt(totalItems, 10);
    if (itemsPerPage !== null) this.itemsPerPage = parseInt(itemsPerPage, 10);
    if (visiblePages !== null) this.visiblePages = parseInt(visiblePages, 10);
  }

  /**
   * Calculate total pages
   */
  get totalPages() {
    const maxAllowedPages = Math.floor(COVEO_MAX_RESULTS / this.itemsPerPage);
    const calculatedPages = Math.ceil(this.totalItems / this.itemsPerPage);
    return Math.min(calculatedPages, maxAllowedPages);
  }

  /**
   * Navigate to a specific page
   */
  goToPage(page) {
    if (page < 0 || page >= this.totalPages) return;
    this.currentPage = page;
    this.setAttribute("current-page", page);

    // Dispatch custom event
    const event = new CustomEvent("page-change", {
      detail: { page: this.currentPage },
      bubbles: true,
      composed: true,
    });

    this.dispatchEvent(event);
    this.render();
  }

  setPager(pager) {
    this.pager = pager;
  }

  /**
   * Render the pagination component
   */
  render() {
    // Clear container
    this.container.innerHTML = "";
    // Don't render if no items or only one page
    if (this.totalItems === 0 || this.totalPages <= 1) {
      this.classList.add("opacity-0");
      return;
    } else {
      this.classList.remove("opacity-0");
    }

    // Previous button
    this.previousButton.disabled = this.currentPage === 0;

    // Get page range using PagerUtils
    const pageNumbers = PagerUtils.getCurrentPagesRange(
      this.currentPage,
      this.visiblePages,
      this.totalPages - 1, // Convert to 0-based for PagerUtils
    );

    // First page button (if not in range)
    if (pageNumbers[0] > 0) {
      const div = document.createElement("div");
      div.innerHTML = this.paginationItemTemplate;

      const firstButton = div.querySelector("button");
      firstButton.textContent = "1";
      firstButton.addEventListener("click", () => this.pager.selectPage(0));
      this.container.appendChild(div.firstElementChild);

      // Add ellipsis if needed
      if (pageNumbers[0] > 1) {
        const ellipsis = document.createElement("span");
        ellipsis.textContent = "...";
        this.container.appendChild(ellipsis);
      }
    }

    // Page number buttons
    pageNumbers.forEach((pageNum) => {
      const div = document.createElement("div");
      div.innerHTML = this.paginationItemTemplate;

      const pageButton = div.querySelector("button");
      pageButton.textContent = pageNum + 1; // Convert to 1-based for display
      div.firstElementChild.classList.toggle("active", pageNum == this.currentPage);
      pageButton.addEventListener("click", () => this.goToPage(pageNum));
      this.container.appendChild(div.firstElementChild);
    });

    // Last page button (if not in range)
    if (pageNumbers[pageNumbers.length - 1] < this.totalPages - 1) {
      // Add ellipsis if needed
      if (pageNumbers[pageNumbers.length - 1] < this.totalPages - 2) {
        const ellipsis = document.createElement("span");
        ellipsis.textContent = "...";
        this.container.appendChild(ellipsis);
      }

      const div = document.createElement("div");
      div.innerHTML = this.paginationItemTemplate;

      const lastButton = div.querySelector("button");
      lastButton.textContent = this.totalPages;
      lastButton.addEventListener("click", () => this.pager.selectPage(this.totalPages - 1));
      this.container.appendChild(div.firstElementChild);
    }

    // Next button
    // const nextButton = document.createElement("button");
    // nextButton.innerHTML = "&rarr;";
    this.nextButton.disabled = this.currentPage >= this.totalPages - 1;
    // nextButton.addEventListener("click", () => this.pager.nextPage());
    // this.container.appendChild(nextButton);
  }

  initButtonsEvent() {
    this.nextButton.addEventListener("click", () => this.goToPage(this.currentPage + 1));
    this.previousButton.addEventListener("click", () => this.goToPage(this.currentPage - 1));
  }
}

// Register the custom element
customElements.define("coveo-pagination", CoveoPagination);

export default CoveoPagination;
